import type { Fit } from "./UtilTypes";

export type CookieType = "functional" | "analytics" | "marketing" | "essential";

export interface CookieInfo {
  name: string;
  description: string;
  expirationDescription: string;
  expirationMs?: number;
  type: CookieType;
  providerName?: string;
  /**
   * The cookie name is a prefix: the real cookie is `<name>_<suffix>` (for example one cookie per
   * asset). Consent checks match both the exact name and the prefixed form.
   */
  isPrefix?: boolean;
}

export const IMPERSONATION_COOKIE_NAME = "CL_impersonation";

const DAY_MS = 24 * 60 * 60 * 1000;
const YEAR_MS = 365 * DAY_MS;

/*
 * Cookie inventory shown on the Cookie Policy page. Functional cookies are only written with
 * consent (see shared/CookieConsent). When you add, remove or re-categorise a cookie here, bump
 * COOKIE_CONSENT_VERSION in shared/CookieConsent.ts so people are asked again.
 */
export const FIRST_PARTY_COOKIES = {
  ACCEPTED_COOKIES_VERSION: {
    name: "CL_acceptedCookiesVersion",
    description:
      "Records your cookie choice: the version of this cookie list you answered and whether you allowed functional and analytics cookies",
    expirationDescription: "1 year",
    expirationMs: YEAR_MS,
    type: "essential",
    providerName: "CalmLens",
  },
  EVER_LOGGED_IN: {
    name: "CL_everLoggedIn",
    description:
      "Remembers which sign-in method you last used on this device so the sign-in page can suggest it",
    expirationDescription: "10 years",
    type: "functional",
    providerName: "CalmLens",
  },
  ID_TOKEN: {
    name: "CL_idToken",
    description: "Authentication token that keeps you signed in (HttpOnly)",
    expirationDescription: "When the token expires (about 1 hour); renewed while you are signed in",
    type: "essential",
    providerName: "CalmLens",
  },
  ID_TOKEN_EXPIRES_AT: {
    name: "CL_idTokenExpiresAt",
    description: "When the current authentication token expires, so it can be renewed in time",
    expirationDescription: "10 years, or until you sign out",
    type: "essential",
    providerName: "CalmLens",
  },
  REFRESH_TOKEN: {
    name: "CL_refreshToken",
    description:
      "Token used to renew your sign-in without asking for your password again (HttpOnly)",
    expirationDescription: "10 years, or until you sign out",
    type: "essential",
    providerName: "CalmLens",
  },
  LOGGED_IN: {
    name: "CL_loggedIn",
    description: "Indicates that you are signed in, so pages can show the signed-in view",
    expirationDescription: "10 years, or until you sign out",
    type: "essential",
    providerName: "CalmLens",
  },
  IMPERSONATION: {
    name: IMPERSONATION_COOKIE_NAME,
    description:
      "Staff support access only: lets an authorised CalmLens staff member view the service as a specific user for support, security, safety or legal reasons. Only honoured for staff accounts and never set for other users",
    expirationDescription: "Session",
    type: "essential",
    providerName: "CalmLens",
  },
  MAINTENANCE_BYPASS: {
    name: "CL_maintenanceBypass",
    description:
      "Staff only: allows authorised staff to use the site while it is in maintenance mode (HttpOnly)",
    expirationDescription: "1 hour",
    expirationMs: 60 * 60 * 1000,
    type: "essential",
    providerName: "CalmLens",
  },
  WAITLIST_BYPASS: {
    name: "CL_waitlistBypass",
    description:
      "Staff only: allows authorised staff to use the site while access is limited to the waitlist (HttpOnly)",
    expirationDescription: "1 hour",
    expirationMs: 60 * 60 * 1000,
    type: "essential",
    providerName: "CalmLens",
  },
  SIGN_IN_CODE: {
    name: "CL_signInCode",
    description:
      "Records that you entered the sign-in code while access is limited to the waitlist, so you can sign in (HttpOnly)",
    expirationDescription: "30 days",
    expirationMs: 30 * DAY_MS,
    type: "essential",
    providerName: "CalmLens",
  },
  SIGN_IN_CODE_ACCEPTED: {
    name: "CL_signInCodeAccepted",
    description:
      "Indicates that you entered the sign-in code, so the sign-in page shows the sign-in options",
    expirationDescription: "30 days",
    expirationMs: 30 * DAY_MS,
    type: "essential",
    providerName: "CalmLens",
  },
  PRE_RENDERED_WIDTH: {
    name: "CL_preRenderedWidth",
    description:
      "Your browser's viewport width, so pages are rendered with the right layout for your screen",
    expirationDescription: "30 days",
    expirationMs: 30 * DAY_MS,
    type: "essential",
    providerName: "CalmLens",
  },
  SELECTED_PROJECT_ID: {
    name: "CL_selectedProjectId",
    description: "Remembers the project you last selected in the portal",
    expirationDescription: "1 year",
    expirationMs: YEAR_MS,
    type: "functional",
    providerName: "CalmLens",
  },
  SELECTED_ORGANISATION_ID: {
    name: "CL_selectedOrganisationId",
    description: "Remembers the organisation you last selected in the portal",
    expirationDescription: "1 year",
    expirationMs: YEAR_MS,
    type: "functional",
    providerName: "CalmLens",
  },
  ASSET_DASHBOARD_STATE: {
    name: "CL_assetDashboardState",
    description: "Remembers your asset dashboard view (view mode, filters, sorting and folder)",
    expirationDescription: "1 year",
    expirationMs: YEAR_MS,
    type: "functional",
    providerName: "CalmLens",
  },
  ASSET_VIEW: {
    name: "CL_assetView",
    description:
      "Remembers whether you chose the grid or table view for an asset's sub-assets (one cookie per asset, named CL_assetView_<assetId>)",
    expirationDescription: "30 days",
    expirationMs: 30 * DAY_MS,
    type: "functional",
    providerName: "CalmLens",
    isPrefix: true,
  },
  PORTAL_GUIDANCE_HIDDEN: {
    name: "CL_portalGuidanceHidden",
    description: "Remembers whether you hid next-step tips for a project",
    expirationDescription: "1 year",
    expirationMs: YEAR_MS,
    type: "functional",
    providerName: "CalmLens",
    isPrefix: true,
  },
  PORTAL_LAYOUT_SPLIT_PANE: {
    name: "CL_portalLayoutSplitPane",
    description: "Remembers the width you set for the portal sidebar",
    expirationDescription: "1 year",
    expirationMs: YEAR_MS,
    type: "functional",
    providerName: "CalmLens",
  },
  DOCS_UI_LIBRARY: {
    name: "CL_docsUiLibrary",
    description: "Remembers the UI library you selected for documentation examples",
    expirationDescription: "1 year",
    expirationMs: YEAR_MS,
    type: "functional",
    providerName: "CalmLens",
  },
  DOCS_LANGUAGE: {
    name: "CL_docsLanguage",
    description: "Remembers the programming language you selected for documentation examples",
    expirationDescription: "1 year",
    expirationMs: YEAR_MS,
    type: "functional",
    providerName: "CalmLens",
  },
  SEARCH_SDK_LANGUAGE: {
    name: "CL_searchSdkLanguage",
    description: "Remembers the SDK you chose to filter documentation search results by",
    expirationDescription: "1 year",
    expirationMs: YEAR_MS,
    type: "functional",
    providerName: "CalmLens",
  },
} as const satisfies Record<string, CookieInfo>;

/*
 * Cookies set by third-party code that runs on our pages. Stripe checkout is a redirect to
 * Stripe-hosted pages (no Stripe.js on our domain), so Stripe's cookies are covered in the Cookie
 * Policy text rather than listed here.
 */
export const THIRD_PARTY_COOKIES = {
  GOOGLE_ANALYTICS: {
    name: "_ga",
    description:
      "Google Analytics distinguishes visitors and maintains session state, only with analytics consent",
    expirationDescription: "2 years",
    type: "analytics",
    providerName: "Google Analytics",
    isPrefix: true,
  },
  GOOGLE_AD_CLICK: {
    name: "_gcl",
    description: "Links consented ad clicks to signups; no personalised advertising",
    expirationDescription: "Up to 90 days",
    type: "marketing",
    providerName: "Google",
    isPrefix: true,
  },
  GOOGLE_CAMPAIGN: {
    name: "_gac",
    description: "Stores consented campaign attribution",
    expirationDescription: "Up to 90 days",
    type: "marketing",
    providerName: "Google",
    isPrefix: true,
  },
} as const satisfies Record<string, CookieInfo>;

/** Single inventory for consent enforcement, the manager and the published policy. */
export const COOKIE_REGISTRY: readonly CookieInfo[] = [
  ...Object.values(FIRST_PARTY_COOKIES),
  ...Object.values(THIRD_PARTY_COOKIES),
];

export const PAYMENT_RETURN_TYPES = ["edit", "account", "settings", "subscription"] as const;
export type PaymentReturnType = (typeof PAYMENT_RETURN_TYPES)[number];

/** Ten years, in seconds (for Max-Age). Multiply by 1000 before using it as milliseconds. */
export const COOKIE_NEVER_EXPIRES = /* @__PURE__ */ (() => 60 * 60 * 24 * 365 * 10)();

export const DEFAULT_FIT: Fit = "contain";
export const MAX_IMAGE_UPLOAD_MB = 20;
export const MAX_IMAGE_UPLOAD_STRING = "20 MiB";
export const MAX_IMAGE_DIMENSION = 12000; // Cloudflare Images limit
export const MAX_IMAGE_UPLOAD_BYTES = /* @__PURE__ */ (() => MAX_IMAGE_UPLOAD_MB * 1024 * 1024)();
export const MAX_DIRECT_IMAGE_UPLOAD_MB = 10;
export const MAX_DIRECT_IMAGE_UPLOAD_STRING = "10 MiB";
export const MAX_DIRECT_IMAGE_UPLOAD_BYTES = /* @__PURE__ */ (() =>
  MAX_DIRECT_IMAGE_UPLOAD_MB * 1024 * 1024)();
export const MIN_MULTIPART_IMAGE_UPLOAD_BYTES = MAX_DIRECT_IMAGE_UPLOAD_BYTES;
export const MIN_MULTIPART_IMAGE_UPLOAD_STRING = MAX_DIRECT_IMAGE_UPLOAD_STRING;
export const MAX_NON_IMAGE_UPLOAD_MB = 5000;
export const MAX_NON_IMAGE_UPLOAD_BYTES = /* @__PURE__ */ (() =>
  MAX_NON_IMAGE_UPLOAD_MB * 1024 * 1024)(); // 5,000 MiB
export const MAX_NON_IMAGE_UPLOAD_STRING = "5,000 MiB";
export const MIN_MULTIPART_UPLOAD_PART_BYTES = /* @__PURE__ */ (() => 5 * 1024 * 1024)();
export const REJECTED_UPLOAD_MIME_TYPES = [
  "application/octet-stream",
  "multipart/form-data",
  "multipart/mixed",
  "multipart/alternative",
  "multipart/related",
  "multipart/signed",
  "multipart/encrypted",
] as const;
export const REJECTED_UPLOAD_MIME_TYPE_SET = /* @__PURE__ */ new Set<string>(
  REJECTED_UPLOAD_MIME_TYPES,
);

export const normalizeUploadMimeType = (mimeType: string) =>
  mimeType.toLowerCase().split(";")[0]?.trim() || mimeType.toLowerCase().trim();

export const isRejectedUploadMimeType = (mimeType: string) =>
  REJECTED_UPLOAD_MIME_TYPE_SET.has(normalizeUploadMimeType(mimeType));

export const getRejectedUploadMimeTypeError = (mimeType: string) =>
  `${normalizeUploadMimeType(mimeType)} is not a valid upload file type. Uploads must use a concrete file MIME type that CalmLens can store and process, such as image/png, image/jpeg, application/pdf, or text/plain.`;

export const DOMAIN = "calmlens.com";
export const TITLE = "CalmLens";
export const COMPANY_ADDRESS =
  "Lytchett House, 13 Freeland Park, Wareham Road, Poole, Dorset, BH16 6FA, United Kingdom";
export const BLURB = "Safe media, simple delivery";
/** The CalmLens account on X; @calmlens was taken. */
export const X_PROFILE_URL = "https://x.com/calm_lens";

// Keep feature switches as literal exports so builds can remove disabled branches: the
// inline-constants build transform (scripts/inline-constants.cjs) replaces imported boolean
// constants from *Constants.ts files with their literal values.
/**
 * Samples a share of allowed and restricted assets into a human review queue, worked by
 * reviewer members who only see that queue in the portal.
 */
export const ENABLE_HUMAN_REVIEW = true;
/** Text documents use file icons while generated image previews are disabled. */
export const ENABLE_PLAINTEXT_PREVIEWS = false;
/** Appeals are implemented but unavailable until explicitly enabled. */
export const ENABLE_APPEALS = false;
/**
 * Person and face detection (`processingOptions.features.personDetection` and `faceDetection`).
 * Off for now: the API rejects them as unknown features, and the portal, pricing and docs hide them.
 */
export const ENABLE_PEOPLE_AND_FACES = false;
/**
 * AI-generated image detection (`processingOptions.features.aiGeneratedDetection`) from the
 * provenance metadata generators attach (C2PA Content Credentials, IPTC digital source type and
 * generator tool metadata). Off for now: the API rejects it as an unknown feature, and the portal
 * and docs hide it.
 */
export const ENABLE_AI_GENERATED_DETECTION = false;
/**
 * Moderates images with Clef Flash as the first fallback after Jev. With its own image curve it
 * gets 12/163 human-reviewed image checks wrong (Jev 3), so it only runs when Jev fails.
 */
export const ENABLE_CLEF_FLASH_IMAGES = true;
/**
 * Link safety (preview): links in text, QR codes and OCR text are followed through short-link
 * redirects and checked against domain categories and threat lists, and the policy's link-safety
 * section acts on them.
 */
export const ENABLE_LINK_SAFETY = true;
/** Publishes /dpa and the links to it from the Terms, Privacy Policy, Trust page and notices. */
export const DPA_PUBLISHED = true;
/** Independent release labels for preview features in the portal, site and documentation. */
export const WEBSITE_IS_PREVIEW = true;
export const AUDIO_IS_PREVIEW = true;
export const VIDEO_IS_PREVIEW = true;
export const PERSON_DETECTION_IS_PREVIEW = true;
export const FACE_DETECTION_IS_PREVIEW = true;
export const PERSONAL_INFORMATION_IS_PREVIEW = false;
export const AI_GENERATED_DETECTION_IS_PREVIEW = true;
export const LINK_SAFETY_IS_PREVIEW = true;
